import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Typography,
  Divider,
  Badge,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Button,
} from '@mui/material';
import { 
  Search as SearchIcon, 
  FilterList as FilterIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format, isToday, isYesterday } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';
import ChatInterface from '../../components/ChatInterface';
import ReportDialog from '../../components/ReportDialog';
import { useAuth } from '../../contexts/AuthContext';
import { messagesApi } from '../../services/api';
import PageBackButton from '../../components/PageBackButton';

// Мокнати данни за разговори
const MOCK_CONVERSATIONS = [
  {
    id: 1,
    user: {
      id: 1,
      name: 'Иван Иванов',
      avatar: 'https://randomuser.me/api/portraits/men/1.jpg',
      online: true
    },
    last_message: {
      content: 'Банята е 6 квадратни метра. Искам цялостен ремонт с подмяна на плочки, ВиК и санитария.',
      created_at: new Date(Date.now() - 3600000 * 22).toISOString(),
      is_read: true,
      sender_id: 1
    },
    unread_count: 0,
    task_id: null
  },
  {
    id: 2,
    user: {
      id: 2,
      name: 'Мария Петрова',
      avatar: 'https://randomuser.me/api/portraits/women/2.jpg',
      online: false
    },
    last_message: {
      content: 'Благодаря за бързия отговор!',
      created_at: new Date(Date.now() - 3600000 * 5).toISOString(),
      is_read: true,
      sender_id: 2
    },
    unread_count: 0,
    task_id: 15
  },
  {
    id: 3,
    user: {
      id: 3,
      name: 'Георги Димитров',
      avatar: 'https://randomuser.me/api/portraits/men/3.jpg',
      online: true
    },
    last_message: {
      content: 'Да, идеално е. Ще ви очаквам. Адресът е ул. Цар Борис III №45.',
      created_at: new Date(Date.now() - 86400000).toISOString(),
      is_read: false,
      sender_id: 3
    },
    unread_count: 1,
    task_id: null
  },
  {
    id: 4,
    user: {
      id: 4,
      name: 'Елена Тодорова',
      avatar: 'https://randomuser.me/api/portraits/women/4.jpg',
      online: false
    },
    last_message: {
      content: 'Имате ли опит с ремонт на покриви?',
      created_at: new Date(Date.now() - 86400000 * 3).toISOString(),
      is_read: true,
      sender_id: 4
    },
    unread_count: 0,
    task_id: 22
  },
  {
    id: 5,
    user: {
      id: 5,
      name: 'Стефан Николов',
      avatar: 'https://randomuser.me/api/portraits/men/5.jpg',
      online: true
    },
    last_message: {
      content: 'Моля изпратете ми оферта за боядисване на апартамент от 90кв.м.',
      created_at: new Date(Date.now() - 86400000 * 5).toISOString(),
      is_read: true,
      sender_id: 5
    },
    unread_count: 0,
    task_id: null
  }
];

interface Conversation {
  id: number;
  user: {
    id: number;
    name: string;
    avatar?: string;
    online: boolean;
  };
  last_message: {
    content: string;
    created_at: string;
    is_read: boolean;
    sender_id: number;
  };
  unread_count: number;
  task_id: number | null;
}

const ChatPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [filteredConversations, setFilteredConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    // Филтрираме разговорите въз основа на търсенето
    if (searchQuery.trim() === '') {
      setFilteredConversations(conversations);
    } else {
      const lowercaseQuery = searchQuery.toLowerCase();
      setFilteredConversations(
        conversations.filter((conversation) =>
          conversation.user.name.toLowerCase().includes(lowercaseQuery)
        )
      );
    }
  }, [searchQuery, conversations]);

  useEffect(() => {
    // Филтрираме разговорите въз основа на избрания таб
    if (activeTabIndex === 0) { // Всички
      setFilteredConversations(conversations);
    } else if (activeTabIndex === 1) { // Непрочетени
      setFilteredConversations(
        conversations.filter((conversation) => conversation.unread_count > 0)
      );
    } else if (activeTabIndex === 2) { // Свързани със задачи
      setFilteredConversations(
        conversations.filter((conversation) => conversation.task_id !== null)
      );
    }
  }, [activeTabIndex, conversations]);

  const fetchConversations = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Извикваме реалния API endpoint за разговори чрез messagesApi
      const data = await messagesApi.getConversations();
      setConversations(data);
      setFilteredConversations(data);
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setConversations([]);
      setFilteredConversations([]);
      setError(t('chat.errors.fetch_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleConversationSelect = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    
    // На мобилни устройства скриваме списъка с разговори
    if (isMobile) {
      // Маркираме разговора като прочетен локално
      setConversations(prevConversations => 
        prevConversations.map(c => 
          c.id === conversation.id 
            ? { ...c, unread_count: 0, last_message: { ...c.last_message, is_read: true } } 
            : c
        )
      );
    }
  };

  const handleBackToList = () => {
    setSelectedConversation(null);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTabIndex(newValue);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const getLocale = () => getDateFnsLocale(i18n.language);

  const formatMessageDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isToday(date)) {
        return format(date, 'HH:mm', { locale: getLocale() });
      } else if (isYesterday(date)) {
        return t('chat.yesterday');
      } else {
        return format(date, 'dd.MM.yyyy', { locale: getLocale() });
      }
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  const getUnreadCount = () => {
    return conversations.reduce((total, conv) => total + conv.unread_count, 0);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4, height: 'calc(100vh - 140px)' }}>
      {(!isMobile || !selectedConversation) && <PageBackButton />}
      <Paper elevation={3} sx={{ height: '100%', overflow: 'hidden' }}>
        <Grid container sx={{ height: '100%' }}>
          {/* Списък с разговори - скриваме на мобилни устройства, когато е избран разговор */}
          {(!isMobile || !selectedConversation) && (
            <Grid item xs={12} md={4} sx={{ borderRight: '1px solid', borderColor: 'divider', height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" component="h2" gutterBottom>
                  {t('chat.conversations')}
                </Typography>
                <TextField
                  fullWidth
                  placeholder={String(t('chat.search'))}
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small">
                          <FilterIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />
                <Tabs
                  value={activeTabIndex}
                  onChange={handleTabChange}
                  variant="fullWidth"
                  aria-label="chat tabs"
                >
                  <Tab 
                    label={t('chat.all')} 
                    id="chat-tab-0"
                    aria-controls="chat-tabpanel-0"
                  />
                  <Tab 
                    label={
                      <Badge badgeContent={getUnreadCount()} color="error" max={99}>
                        {t('chat.unread')}
                      </Badge>
                    } 
                    id="chat-tab-1"
                    aria-controls="chat-tabpanel-1"
                    disabled={getUnreadCount() === 0}
                  />
                  <Tab 
                    label={t('chat.tasks')} 
                    id="chat-tab-2"
                    aria-controls="chat-tabpanel-2"
                  />
                </Tabs>
              </Box>
              
              <Box sx={{ 
                overflow: 'auto', 
                flexGrow: 1,
                bgcolor: 'background.default'
              }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : error ? (
                  <Alert severity="error" sx={{ m: 2 }}>
                    {error}
                  </Alert>
                ) : filteredConversations.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      {searchQuery ? t('chat.no_results') : t('chat.no_conversations')}
                    </Typography>
                    <Button 
                      variant="outlined" 
                      sx={{ mt: 2 }}
                      onClick={fetchConversations}
                    >
                      {t('chat.refresh')}
                    </Button>
                  </Box>
                ) : (
                  <List sx={{ p: 0 }} data-testid="conversation-list">
                    {filteredConversations.map((conversation) => (
                      <React.Fragment key={conversation.id}>
                        <ListItem 
                          button 
                          selected={selectedConversation?.id === conversation.id}
                          onClick={() => handleConversationSelect(conversation)}
                          data-testid={`conversation-item-${conversation.id}`}
                          sx={{ 
                            bgcolor: conversation.unread_count > 0 ? 'primary.light' : 'transparent',
                            '&.Mui-selected': {
                              bgcolor: 'primary.main',
                              color: 'primary.contrastText',
                              '&:hover': {
                                bgcolor: 'primary.dark',
                              }
                            },
                          }}
                        >
                          <ListItemAvatar>
                            <Badge
                              overlap="circular"
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                              variant="dot"
                              color={conversation.user.online ? "success" : "default"}
                            >
                              <Avatar 
                                alt={conversation.user.name} 
                                src={conversation.user.avatar}
                                imgProps={{
                                  onError: (e) => {
                                    (e.currentTarget as HTMLImageElement).src = '';
                                  },
                                }}
                              >
                                {conversation.user.name?.[0]?.toUpperCase() || '?'}
                              </Avatar>
                            </Badge>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span 
                                  style={{ 
                                    fontWeight: conversation.unread_count > 0 ? 'bold' : 'normal',
                                    color: selectedConversation?.id === conversation.id ? 'inherit' : undefined,
                                    fontSize: '1rem'
                                  }}
                                >
                                  {conversation.user.name}
                                </span>
                                <span 
                                  style={{ 
                                    color: selectedConversation?.id === conversation.id ? 'inherit' : '#666',
                                    fontSize: '0.75rem'
                                  }}
                                >
                                  {formatMessageDate(conversation.last_message.created_at)}
                                </span>
                              </span>
                            }
                            secondary={
                              <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span
                                  style={{ 
                                    color: selectedConversation?.id === conversation.id ? 'inherit' : '#888',
                                    opacity: selectedConversation?.id === conversation.id ? 0.9 : 0.7,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: 'vertical' as const,
                                    width: '85%',
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  {user?.id === conversation.last_message.sender_id && (
                                    <span>{t('chat.you')}: </span>
                                  )}
                                  {conversation.last_message.content}
                                </span>
                                {conversation.unread_count > 0 && (
                                  <Badge 
                                    badgeContent={conversation.unread_count} 
                                    color="error" 
                                    max={99}
                                    sx={{ 
                                      '& .MuiBadge-badge': {
                                        color: 'white',
                                      }
                                    }}
                                  />
                                )}
                              </span>
                            }
                          />
                        </ListItem>
                        <Divider component="li" />
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            </Grid>
          )}
          
          {/* Интерфейс на разговора */}
          <Grid item xs={12} md={8} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {isMobile && selectedConversation && (
              <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <IconButton onClick={handleBackToList}>
                  <ArrowBackIcon />
                </IconButton>
              </Box>
            )}
            
            {selectedConversation ? (
              <>
                <Box
                  sx={{
                    p: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    onClick={() => setIsReportDialogOpen(true)}
                  >
                    {t('admin.reports.report_profile_button')}
                  </Button>
                </Box>
                <ChatInterface 
                  otherUserId={selectedConversation.user.id} 
                  otherUserName={selectedConversation.user.name} 
                  otherUserAvatarUrl={selectedConversation.user.avatar}
                  taskId={selectedConversation.task_id || undefined}
                />
              </>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  bgcolor: 'background.default',
                  p: 3,
                }}
              >
                <Typography variant="h5" gutterBottom>
                  {t('chat.select_conversation')}
                </Typography>
                <Typography variant="body1" color="text.secondary" textAlign="center">
                  {t('chat.select_conversation_description')}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>
      {selectedConversation && user && (
        <ReportDialog
          open={isReportDialogOpen}
          onClose={() => setIsReportDialogOpen(false)}
          targetType="profile"
          targetId={selectedConversation.user.id}
          targetLabel={t('admin.reports.target_label_profile', { id: selectedConversation.user.id })}
        />
      )}
    </Container>
  );
};

export default ChatPage; 